import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { login as apiLogin, getMe } from '../services/api';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      getMe()
        .then(res => setUser(res.data))
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    const res = await apiLogin(username, password);
    localStorage.setItem('token', res.data.access_token);
    const userRes = await getMe();
    setUser(userRes.data);
    return userRes.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  // Vuelve a leer /api/auth/me. Necesario después de que un admin se edite a sí
  // mismo: si no, el menú sigue mostrando lo que ya no puede hacer hasta que
  // recargue la página.
  const refresh = async () => {
    const res = await getMe();
    setUser(res.data);
    return res.data;
  };

  const permisos = useMemo(
    () => new Set(user?.permissions || []),
    [user]
  );

  /**
   * ¿El usuario tiene TODOS estos permisos?
   *
   * Devuelve true si el backend reporta rbac_enabled=false — o sea, si la
   * migración 0009 no está aplicada. El backend hace lo mismo, así que la UI y
   * la API coinciden en lugar de esconder botones que sí funcionarían.
   *
   * Esto es solo cosmética: esconde lo que no sirve. Quien llame a la API a
   * mano se topa igual con el 403. La autorización real vive en el backend.
   */
  const can = (...codes) => {
    if (!user) return false;
    if (user.rbac_enabled === false) return true;
    return codes.every(c => permisos.has(c));
  };

  /** ¿Tiene AL MENOS UNO de estos permisos? Útil para secciones del menú. */
  const canAny = (...codes) => {
    if (!user) return false;
    if (user.rbac_enabled === false) return true;
    return codes.some(c => permisos.has(c));
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refresh, can, canAny }}>
      {children}
    </AuthContext.Provider>
  );
};
